import styles from "./style";
import { useEffect, useState, useRef } from "react";
import { Routes, Route, BrowserRouter, useLocation, Navigate } from 'react-router-dom';
import Home from './pages/Home';
import Contact from "./pages/Contact";
import Expertise from "./pages/Expertise";
import Products from "./pages/Products";
import About from "./pages/About";
import Navbar from "./components/Navbar";
import Publication from "./pages/Publication";
import Loader from "./components/Loader";
import { AnimatePresence } from "framer-motion";
import ProductDetail from "./pages/components/ProductDetail";
import BlogDetail from "./components/BlogDetail";
import BlogList from "./components/BlogList";
import Login from "./components/user/login";
import BlogWrite from "./components/BlogWrite";
import { LogoGif, LogoName1, LogoIcon, contact1, contact, LukuFiti, GoSoft, SafiriSalama, Rembeka, f1, c1, goSoft, img2, img3, img4, p2 } from "./assets";

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin }) => {
  const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
  const isAdmin = localStorage.getItem('isAdmin') === 'true';
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/blog" replace />;
  }
  
  return children;
};

function AnimatedRoutes() {
  const location = useLocation()

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Home />} />
        <Route path="/Expertise" element={<Expertise />} />
        <Route path="/Product" element={<Products />} />
        <Route path="/About" element={<About />} />
        <Route path="/Publication" element={<Publication />} />
        <Route path="/BlogDetail/:id" element={<BlogDetail />} />
        <Route path="/BlogWrite" element={<BlogWrite />} />
        <Route path="/Contact" element={<Contact />} /> 
        <Route path="/Product" element={<ProductDetail />} />
        <Route path="/Login" element={<Login />} />
        
        {/* Protected Admin Route - Blog Dashboard */}
        <Route 
          path="/BlogWrite" 
          element={
            <ProtectedRoute requireAdmin={true}>
              <BlogWrite />
            </ProtectedRoute>
          } 
        />
      </Routes>
    </AnimatePresence>
  )
}

// Preload utility function
const preloadImages = (imageUrls, onProgress) => {
  const totalImages = imageUrls.length;
  let loadedImages = 0;

  const loadImage = (src) => {
    return new Promise((resolve) => {
      if (!src || typeof src !== 'string') {
        loadedImages++;
        onProgress?.(Math.round((loadedImages / totalImages) * 100));
        resolve();
        return;
      }

      const img = new Image();
      
      img.onload = () => {
        loadedImages++;
        onProgress?.(Math.round((loadedImages / totalImages) * 100));
        resolve();
      };
      
      img.onerror = () => {
        loadedImages++;
        onProgress?.(Math.round((loadedImages / totalImages) * 100));
        resolve(); // Continue even if some images fail
      };

      img.src = src;
    });
  };

  return Promise.all(imageUrls.map(loadImage));
};

function App() {
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showContent, setShowContent] = useState(false);
  const preloadedRef = useRef(false);

  useEffect(() => {
    // Prevent double preloading in development (React Strict Mode)
    if (preloadedRef.current) return;
    preloadedRef.current = true;

    const preloadAllImages = async () => {
      // Collect ALL images used throughout your application
      const imagesToPreload = [
        // Loader images
        LogoGif,
        LogoName1,
        LogoIcon,
        
        // Slider images
        contact1,
        
        // Site images
        contact,
        LukuFiti,
        GoSoft,
        SafiriSalama,
        Rembeka,
        f1,
        c1,
        img2,
        img3,
        img4,
        
        // About
        p2,
      ].filter(Boolean);

      try {
        console.log(`Starting to preload ${imagesToPreload.length} images...`);
        
        // Minimum loading time for smooth transition (2.1 seconds)
        const minLoadTime = 2100;
        const startTime = Date.now();

        // Preload all images with progress tracking
        await preloadImages(imagesToPreload, (progress) => {
          setLoadingProgress(progress);
        });

        // Ensure minimum loading time for smooth UX
        const elapsed = Date.now() - startTime;
        if (elapsed < minLoadTime) {
          await new Promise(resolve => setTimeout(resolve, minLoadTime - elapsed));
        }

        console.log('All images preloaded successfully!');
        
        // Smooth transition
        setShowContent(true);
        
        // Remove loader after transition
        setTimeout(() => {
          setIsLoading(false);
        }, 600);

      } catch (error) {
        console.error("Image preloading failed:", error);
        // Still show content even if preloading fails
        setShowContent(true);
        setTimeout(() => {
          setIsLoading(false);
        }, 500);
      }
    };

    preloadAllImages();
  }, []);

  // Show loader while images are being preloaded
  if (isLoading) {
    return <Loader onComplete={() => {}} progress={loadingProgress} />;
  }

  return (
    <div className={`transition-opacity duration-700 ${showContent ? 'opacity-100' : 'opacity-0'}`}>
      <BrowserRouter className={`bg-primary w-full overflow-hidden ${styles.paddingX} ${styles.flexCenter}`}>
        <Navbar />
        <AnimatedRoutes />
      </BrowserRouter>
    </div>
  );
}

export default App;